import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {faCalendar, faTimes} from '@fortawesome/free-solid-svg-icons';
import {FormControl, FormGroup} from '@angular/forms';
import {NgbDate, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {CustomDateFormatter} from "../../custom-date-formatter";

@Component({
    selector: 'app-date-period-picker',
    templateUrl: './date-period-picker.component.html',
    providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateFormatter }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatePeriodPickerComponent implements OnInit {

    @Input() inputLabel: string | null;

    @Input() fromMinDate: NgbDateStruct;
    @Input() fromMaxDate: NgbDateStruct;

    @Input() toMinDate: NgbDateStruct;
    @Input() toMaxDate: NgbDateStruct;

    @Input() isFromRequired: boolean = false;

    @Input() required: boolean | null;

    @Input() periodControl: FormGroup<DatePeriod>;

    @Output() dateFromSelected = new EventEmitter<NgbDate>();
    @Output() dateToSelected = new EventEmitter<NgbDate>();
    @Output() dateFromReset = new EventEmitter<null>();
    @Output() dateToReset = new EventEmitter<null>();

    icons = {
        calendar: faCalendar,
        times: faTimes
    };

    ngOnInit(): void {
        if (!this.inputLabel) {
            this.inputLabel = 'common.form.date-period-picker.defaultLabel';
        }
    }

    resetFromDate(): void {
        this.from.setValue(null);
        this.from.markAsDirty();
        this.dateFromReset.emit(null)
    }

    resetToDate(): void {
        this.to.setValue(null);
        this.to.markAsDirty();
        this.dateToReset.emit(null)
    }

    get from(): FormControl<NgbDateStruct> {
        return this.periodControl.get('from') as FormControl<NgbDateStruct>;
    }

    get to(): FormControl<NgbDateStruct> {
        return this.periodControl.get('to') as FormControl<NgbDateStruct>;
    }

    // Cross-bind the selected "to" value as the FROM picker's max, but never below fromMinDate:
    // ng-bootstrap's checkMinBeforeMax() throws on open if min > max (e.g. editing an elapsed period).
    get fromMaxBound(): NgbDateStruct {
        return this.to.value && this.isAfterOrEqual(this.to.value, this.fromMinDate) ? this.to.value : this.fromMaxDate;
    }

    // Symmetrically: use the selected "from" value as the TO picker's min only if it stays <= toMaxDate.
    get toMinBound(): NgbDateStruct {
        return this.from.value && this.isBeforeOrEqual(this.from.value, this.toMaxDate) ? this.from.value : this.toMinDate;
    }

    // A null/undefined bound means "no constraint" -> the value is always acceptable.
    private isAfterOrEqual(date: NgbDateStruct, bound: NgbDateStruct): boolean {
        if (!bound) {
            return true;
        }
        const d = NgbDate.from(date);
        const b = NgbDate.from(bound);
        return d.after(b) || d.equals(b);
    }

    private isBeforeOrEqual(date: NgbDateStruct, bound: NgbDateStruct): boolean {
        if (!bound) {
            return true;
        }
        const d = NgbDate.from(date);
        const b = NgbDate.from(bound);
        return d.before(b) || d.equals(b);
    }

}

export interface DatePeriod {
    from: FormControl<NgbDateStruct>,
    to: FormControl<NgbDateStruct>
}
